/* ── SPA router with desktop ↔ portfolio slide transition ──────
   Maps:
     /              → #view-desktop
     /work          → #view-portfolio
     /work/<slug>   → #view-case-study  (dataset.slug carries the slug)
   Animation: desktop slides down by calc(100vh - 38px) (top-bar
   overlaps portfolio bottom-bar), then fades out. Reverse on close.
   Initial loads and case-study transitions are not animated.
   ──────────────────────────────────────────────────────────── */
'use strict';
const VIEWS = {
  desktop:    document.getElementById('view-desktop'),
  portfolio:  document.getElementById('view-portfolio'),
  caseStudy:  document.getElementById('view-case-study'),
};
const SLIDE_MS = 600;
const FADE_MS  = 250;

function resolve(path) {
  const clean = (path || '/').replace(/\/+$/, '') || '/';
  if (clean === '/') return { view: 'desktop' };
  if (clean === '/work') return { view: 'portfolio' };
  const m = clean.match(/^\/work\/([^/]+)$/);
  if (m) return { view: 'caseStudy', slug: m[1] };
  return { view: 'desktop' };
}

function notifyLightRays(active) {
  try {
    if (active) { window.__lightRaysStart && window.__lightRaysStart(); }
    else        { window.__lightRaysStop  && window.__lightRaysStop();  }
  } catch (e) { /* noop */ }
}

/* Resolves on first matching transitionend for `propertyName`,
   or after `fallbackMs` (whichever first). Avoids hangs if a
   transition is interrupted or the property never animates. */
function onTransitionEnd(el, propertyName, fallbackMs) {
  return new Promise(function (resolve) {
    let done = false;
    function finish() {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', handler);
      resolve();
    }
    function handler(e) {
      if (e.target !== el) return;
      if (propertyName && e.propertyName !== propertyName) return;
      finish();
    }
    el.addEventListener('transitionend', handler);
    setTimeout(finish, fallbackMs);
  });
}

function clearAnimClasses() {
  VIEWS.desktop.classList.remove(
    'is-sliding-down', 'is-sliding-up',
    'is-slid-down',
    'is-fading-out', 'is-fading-in', 'is-faded-out',
    'is-transitioning',
    'is-hidden'
  );
  VIEWS.portfolio.classList.remove('is-transitioning');
}

/* Non-animated state setter — used for initial render, case study
   navigation, and any other route change that shouldn't animate.
   The portfolio stays mounted (display:'') at all times so the
   slide reveal is seamless; we just toggle .is-inactive to keep
   it from intercepting clicks while the desktop is on top. */
function applyStateImmediate(route) {
  clearAnimClasses();
  /* Drop the pre-paint guard added by the inline script in <head>.
     Once the router is in control, stylesheet display:none on the
     desktop would otherwise stick (router uses style.display = ''
     to show, which falls back to !important stylesheet rules). */
  document.documentElement.classList.remove('direct-load-non-desktop');
  if (route.view === 'desktop') {
    VIEWS.desktop.style.display   = '';
    VIEWS.portfolio.style.display = '';
    VIEWS.portfolio.classList.add('is-inactive');
    VIEWS.caseStudy.style.display = 'none';
  } else if (route.view === 'portfolio') {
    VIEWS.desktop.style.display   = 'none';
    VIEWS.portfolio.style.display = '';
    VIEWS.portfolio.classList.remove('is-inactive');
    VIEWS.caseStudy.style.display = 'none';
  } else if (route.view === 'caseStudy') {
    VIEWS.desktop.style.display   = 'none';
    VIEWS.portfolio.style.display = 'none';
    VIEWS.portfolio.classList.add('is-inactive');
    /* Hide the scroller AND suppress its smooth-scroll behavior
       while we pin the scrollTop. The black case-study
       background paints immediately (no white flash); the
       scroller content appears once it's settled at the right
       section. We don't touch scroll-snap-type — only the
       scroll-behavior, which is what causes the visible
       travel animation. */
    const csScroller = VIEWS.caseStudy.querySelector('.cs-scroller');
    if (csScroller) {
      csScroller.style.visibility     = 'hidden';
      csScroller.style.scrollBehavior = 'auto';
    }
    VIEWS.caseStudy.style.display = '';
    VIEWS.caseStudy.dataset.slug  = route.slug || '';
    if (route.slug && window.__caseStudyScrollTo) {
      window.__caseStudyScrollTo(route.slug, 'auto');
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (csScroller) {
          csScroller.style.visibility     = '';
          csScroller.style.scrollBehavior = '';
        }
      });
    });
  }
  notifyLightRays(route.view === 'portfolio');
}
/* Exposed for the FLIP module so it can swap views mid-FLIP
   without duplicating the display/visibility orchestration. */
window.__applyRouteImmediate = applyStateImmediate;

/* Show the case study ON TOP of the still-visible portfolio,
   without hiding the portfolio. The portfolio acts as a stable
   background under the fade-in; we only hide it once the case
   study is fully opaque. Used by __csFlipOpen Phase 2. */
window.__showCaseStudyOver = function (slug) {
  VIEWS.caseStudy.style.display = '';
  VIEWS.caseStudy.dataset.slug = slug || '';
  if (slug && window.__caseStudyScrollTo) {
    window.__caseStudyScrollTo(slug, 'auto');
  }
};

/* After the fade-in completes, hide the portfolio (it's now
   fully covered by the case study). Updates currentRoute and
   pauses the WebGL light-rays loop. */
window.__hidePortfolioForCaseStudy = function (slug) {
  VIEWS.portfolio.style.display = 'none';
  VIEWS.portfolio.classList.add('is-inactive');
  notifyLightRays(false);
  currentRoute = { view: 'caseStudy', slug: slug };
};

/* Mount the portfolio UNDER the still-visible case-study so the
   reverse FLIP has a target to land into. Light rays start
   BEFORE the cover cards animate back, so the background is
   already alive when they appear. Used by __csFlipClose. */
window.__showPortfolioUnderCaseStudy = function () {
  VIEWS.portfolio.style.display = '';
  VIEWS.portfolio.classList.remove('is-inactive');
  notifyLightRays(true);
};

/* Finalise the reverse FLIP: hide the case study (its content
   has already faded out and the clone is on top of the
   portfolio). Updates currentRoute. */
window.__hideCaseStudyForPortfolio = function () {
  VIEWS.caseStudy.style.display = 'none';
  VIEWS.caseStudy.dataset.slug = '';
  currentRoute = { view: 'portfolio' };
};

/* Called by the case-study scroll observer when scroll-driven
   URL updates change the active project. Keeps currentRoute
   in sync so a subsequent back navigation lands at /work, not
   at the originally-pushed slug. */
window.__caseStudySlugChanged = function (slug) {
  if (currentRoute && currentRoute.view === 'caseStudy') {
    currentRoute.slug = slug;
    VIEWS.caseStudy.dataset.slug = slug;
  }
};

let animating = false;

/* Wait for the next paint. Two rAFs guarantee the browser has
   composited the previous frame before we resolve, so when we
   resolve we know the new portfolio markup is on screen. */
function nextPaint() {
  return new Promise(function (resolve) {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { resolve(); });
    });
  });
}

async function runOpen() {
  if (animating) return;
  animating = true;

  /* Portfolio is already mounted (we keep it mounted at all
     times). Activate it and start the rays before the slide
     begins so it's fully painted underneath. */
  VIEWS.portfolio.classList.remove('is-inactive');
  notifyLightRays(true);
  void VIEWS.portfolio.offsetWidth;
  await nextPaint();

  VIEWS.desktop.classList.add('is-transitioning');
  VIEWS.portfolio.classList.add('is-transitioning');

  VIEWS.desktop.classList.add('is-sliding-down');
  void VIEWS.desktop.offsetWidth;
  VIEWS.desktop.classList.add('is-slid-down');
  await onTransitionEnd(VIEWS.desktop, 'transform', SLIDE_MS + 100);
  VIEWS.desktop.classList.remove('is-sliding-down');

  VIEWS.desktop.classList.add('is-fading-out');
  void VIEWS.desktop.offsetWidth;
  VIEWS.desktop.classList.add('is-faded-out');
  await onTransitionEnd(VIEWS.desktop, 'opacity', FADE_MS + 100);
  VIEWS.desktop.classList.remove('is-fading-out');

  /* Desktop is now opacity:0 above the portfolio. Block its
     pointer events so clicks reach the portfolio bottom-bar. */
  VIEWS.desktop.classList.add('is-hidden');
  VIEWS.desktop.classList.remove('is-transitioning');
  VIEWS.portfolio.classList.remove('is-transitioning');
  animating = false;
}

async function runClose() {
  if (animating) return;
  animating = true;

  /* Pre-stage: works whether we're closing after a real open
     (desktop already slid+faded) or after a direct /work load
     (desktop was display:none with no animation classes). */
  VIEWS.desktop.classList.remove('is-hidden');
  VIEWS.desktop.style.display = '';
  VIEWS.desktop.classList.add('is-slid-down', 'is-faded-out');
  void VIEWS.desktop.offsetWidth;

  VIEWS.desktop.classList.add('is-transitioning');
  VIEWS.portfolio.classList.add('is-transitioning');

  VIEWS.desktop.classList.add('is-fading-in');
  void VIEWS.desktop.offsetWidth;
  VIEWS.desktop.classList.remove('is-faded-out');
  await onTransitionEnd(VIEWS.desktop, 'opacity', FADE_MS + 100);
  VIEWS.desktop.classList.remove('is-fading-in');

  VIEWS.desktop.classList.add('is-sliding-up');
  void VIEWS.desktop.offsetWidth;
  VIEWS.desktop.classList.remove('is-slid-down');
  await onTransitionEnd(VIEWS.desktop, 'transform', SLIDE_MS + 100);
  VIEWS.desktop.classList.remove('is-sliding-up');

  /* Portfolio stays mounted for next time but is now fully
     covered by the desktop — disable its pointer events and
     pause the WebGL loop. */
  VIEWS.portfolio.classList.add('is-inactive');
  notifyLightRays(false);
  VIEWS.desktop.classList.remove('is-transitioning');
  VIEWS.portfolio.classList.remove('is-transitioning');
  animating = false;
}

/* Independent flag for the FLIP transitions so a click during
   the FLIP can't kick off another one. The desktop↔portfolio
   slide uses `animating`; this is its case-study counterpart. */
let csAnimating = false;
function prefersReducedMotion() {
  try {
    return window.matchMedia &&
           window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { return false; }
}

function transitionBetween(prev, next) {
  if (prev.view === 'desktop' && next.view === 'portfolio') {
    runOpen();
  } else if (prev.view === 'portfolio' && next.view === 'desktop') {
    runClose();
  } else if (prev.view === 'portfolio' && next.view === 'caseStudy') {
    if (csAnimating) return;
    const src = window.__pendingFlipSource;
    window.__pendingFlipSource = null;
    if (src && !prefersReducedMotion() && window.__csFlipOpen) {
      csAnimating = true;
      window.__csFlipOpen(next.slug, src).then(function () {
        csAnimating = false;
      }, function () { csAnimating = false; });
    } else {
      applyStateImmediate(next);
    }
  } else if (prev.view === 'caseStudy' && next.view === 'portfolio') {
    if (csAnimating) return;
    if (!prefersReducedMotion() && window.__csFlipClose) {
      csAnimating = true;
      window.__csFlipClose(prev.slug).then(function () {
        csAnimating = false;
      }, function () { csAnimating = false; });
    } else {
      applyStateImmediate(next);
    }
  } else {
    applyStateImmediate(next);
  }
}

let currentRoute = resolve(location.pathname);

function navigateTo(path) {
  const next = resolve(path);
  const prev = currentRoute;
  if (prev.view === next.view && prev.slug === next.slug) return;
  /* If a transition is already in flight, drop the navigation
     entirely. Without this, `transitionBetween` early-returns
     (animating / csAnimating guards) but `currentRoute` still
     advances + `history.pushState` still runs — leaving the URL
     and routing state out of sync with the visible view. */
  if (animating || csAnimating) return;
  history.pushState({ path: path }, '', path);
  transitionBetween(prev, next);
  currentRoute = next;
}
window.navigateTo = navigateTo;

/* Initial render — never animated. */
applyStateImmediate(currentRoute);

/* Back/forward — animated when going between desktop and portfolio. */
window.addEventListener('popstate', function () {
  const next = resolve(location.pathname);
  const prev = currentRoute;
  if (prev.view === next.view && prev.slug === next.slug) return;
  if (animating || csAnimating) return;
  transitionBetween(prev, next);
  currentRoute = next;
});

/* Intercept clicks on internal links. The real `e.target` is
   often a child element (<img> inside <a class="cover-item">,
   <svg> inside a button — but buttons are skipped via the !a
   guard), so we use closest('a') to find the nav anchor. Call
   `preventDefault()` UNCONDITIONALLY and FIRST for any same-
   origin internal anchor — the SPA always handles these — and
   only then decide whether to run the navigation. Bailing out
   without preventDefault leaves the browser to do a real
   navigation, which is the iOS "X tap reloads /work" symptom. */
document.addEventListener('click', function (e) {
  /* Don't override clicks already handled by something else. */
  if (e.defaultPrevented) return;
  /* Modifier keys / non-primary buttons → let the browser open
     in a new tab / window. e.button on touch-derived clicks is
     always 0 so this still works on mobile. */
  if (e.button !== 0) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

  const a = e.target && e.target.closest && e.target.closest('a');
  if (!a) return;
  /* target="_blank" / target="_top" etc. → let the browser handle. */
  if (a.target && a.target !== '_self') return;
  if (a.hasAttribute('download')) return;

  const href = a.getAttribute('href');
  /* Only handle root-relative internal hrefs. External (http(s)://,
     //, mailto:, tel:) and protocol-relative URLs pass through. */
  if (!href || !href.startsWith('/') || href.startsWith('//')) return;

  /* PREVENT FIRST. Past this point the browser MUST NOT do its
     own navigation regardless of what happens below. */
  e.preventDefault();

  /* If a transition is already in flight, drop the navigation
     entirely (URL + state stay consistent). preventDefault has
     already run so the browser won't navigate either. */
  if (animating || csAnimating) return;

  /* If this is a click on a portfolio cover, stash the cover's
     <img> so the FLIP module can read its First rect. We only
     set it for cover-item anchors; cleared by transitionBetween
     after consumption (or on the next click that isn't one). */
  if (a.classList && a.classList.contains('cover-item')) {
    window.__pendingFlipSource = a.querySelector('img');
  } else {
    window.__pendingFlipSource = null;
  }
  navigateTo(href);
});
